import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useController, useFieldArray, useWatch } from 'react-hook-form';
import { PiTrashBold } from 'react-icons/pi';

import { cn } from '#/lib/utils';
import {
  Button,
  DESTRUCTIVE_GHOST_BUTTON_CLASS,
} from '#/modules/commonComponents/button';
import ListFieldMobileLabel from '#/modules/commonComponents/form/ListFieldMobileLabel';
import PlainRegisteredInput from '#/modules/commonComponents/form/PlainRegisteredInput';
import { SelectCore } from '#/modules/commonComponents/form/select';
import { getNestedError } from '#/modules/commonComponents/form/formFieldUtils';
import MaterialNameStack from '#/modules/imsMgmt/receipts/shared/MaterialNameStack';
import { useMaterialUnitOptions } from '#/modules/imsMgmt/helper/materialUnit';
import MtrPickerDialog from '#/modules/imsMgmt/settings/mtr/components/MtrPickerDialog';
import {
  mergePurchaseOrderLinesFromPickerSelection,
} from '#/modules/srmMgmt/purchaseOrder/helper/formSerialize';
import {
  PURCHASE_ORDER_LINES_TIP,
} from '#/modules/srmMgmt/purchaseOrder/schemas/purchaseOrderSchema';

// eslint-disable-next-line max-len -- Tailwind arbitrary grid 須單一完整 class 字面量
const LINE_GRID_COLS = '@xl/form-section:grid-cols-[minmax(10rem,1fr)_minmax(0,14rem)_minmax(0,7rem)_minmax(0,7rem)_minmax(8rem,1fr)_2.5rem]';

const LINES_MIN_W = cn(
  'w-full',
  '@xl/form-section:min-w-[48rem]',
);

const LINE_HEADER_CLASS = cn(
  'sticky top-0 z-10 hidden w-full gap-3 border-b border-gray-300 bg-gray-100 px-3 py-2',
  'text-xs font-medium text-gray-600',
  '@xl/form-section:grid',
  LINE_GRID_COLS,
);

const LINE_ROW_CLASS = cn(
  'grid w-full grid-cols-1 items-center gap-3 px-3 py-3',
  LINE_GRID_COLS,
);

const PurchaseOrderLineRow = ({
  index,
  control,
  register,
  errors,
  line,
  setValue,
  onRemove,
}) => {
  const mtrId = String(line.mtrId ?? '').trim();
  const unitIdField = `lines.${index}.unitId`;
  const orderQtyId = `lines.${index}.orderQty`;
  const unitPriceId = `lines.${index}.unitPrice`;
  const remarkId = `lines.${index}.remark`;

  const initialUnitOptions = Array.isArray(line.unitOptions) && line.unitOptions.length > 0
    ? line.unitOptions
    : null;

  const {
    selectOptions: unitSelectOptions,
    defaultUnitId,
    isLoading: unitOptionsLoading,
  } = useMaterialUnitOptions({
    mtrId,
    initialOptions: initialUnitOptions,
    enabled: Boolean(mtrId),
  });

  const unitDisabled = !mtrId || unitOptionsLoading;
  const unitError = getNestedError(errors, unitIdField);

  const { field: unitField } = useController({
    name: unitIdField,
    control,
    defaultValue: '',
  });

  useEffect(() => {
    if (!mtrId || !defaultUnitId || unitOptionsLoading) {
      return;
    }
    if (String(line.unitId ?? '').trim()) {
      return;
    }
    setValue(unitIdField, defaultUnitId, {
      shouldDirty: false,
      shouldValidate: false,
    });
  }, [
    mtrId,
    defaultUnitId,
    unitOptionsLoading,
    line.unitId,
    unitIdField,
    setValue,
  ]);

  return (
    <li className={LINE_ROW_CLASS}>
      <div className="min-w-0">
        <p className="@xl/form-section:hidden text-xs text-gray-500">物料</p>
        <MaterialNameStack name={line.mtrName} code={line.mtrNo} />
        <input type="hidden" {...register(`lines.${index}.mtrId`)} />
        <input type="hidden" {...register(`lines.${index}.mtrNo`)} />
        <input type="hidden" {...register(`lines.${index}.mtrName`)} />
      </div>
      <div className="min-w-0 w-full max-w-[14rem]">
        <ListFieldMobileLabel
          hasError={Boolean(unitError)}
          className="@xl/form-section:hidden"
        >
          單位
        </ListFieldMobileLabel>
        <SelectCore
          id={unitIdField}
          options={unitSelectOptions}
          value={unitField.value}
          onChange={unitField.onChange}
          onBlur={unitField.onBlur}
          isDisabled={unitDisabled}
          noOptionsMessage={
            !mtrId ? '請先選擇物料' : '此物料尚無可選單位'
          }
          placeholder="單位"
          errors={errors}
          hideErrorMessage
          isClearable
          aria-label={`單位 ${index + 1}`}
          aria-invalid={unitError ? true : undefined}
          title={unitError?.message || undefined}
          className="w-full"
        />
      </div>
      <div className="min-w-0">
        <PlainRegisteredInput
          id={orderQtyId}
          type="number"
          min={1}
          step="any"
          register={register}
          errors={errors}
          mobileLabel="訂購數量"
          mobileLabelClassName="@xl/form-section:hidden"
          ariaLabel={`訂購數量 ${index + 1}`}
        />
      </div>
      <div className="min-w-0">
        <PlainRegisteredInput
          id={unitPriceId}
          type="number"
          min={0}
          step="any"
          register={register}
          errors={errors}
          mobileLabel="單價"
          mobileLabelClassName="@xl/form-section:hidden"
          ariaLabel={`單價 ${index + 1}`}
        />
      </div>
      <div className="min-w-0">
        <PlainRegisteredInput
          id={remarkId}
          type="text"
          register={register}
          errors={errors}
          mobileLabel="行備註"
          mobileLabelClassName="@xl/form-section:hidden"
          ariaLabel={`行備註 ${index + 1}`}
        />
      </div>
      <div className="flex min-w-0 @xl/form-section:justify-end">
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className={DESTRUCTIVE_GHOST_BUTTON_CLASS}
          aria-label={`移除明細 ${index + 1}`}
          onClick={() => onRemove(index)}
        >
          <PiTrashBold className="size-4" />
        </Button>
      </div>
    </li>
  );
};

PurchaseOrderLineRow.propTypes = {
  index: PropTypes.number.isRequired,
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object,
  line: PropTypes.object.isRequired,
  setValue: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

const PurchaseOrderLinesEditor = ({
  control,
  register,
  errors,
  getValues,
  setValue,
}) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerKey, setPickerKey] = useState(0);

  const { fields, remove, replace } = useFieldArray({
    control,
    name: 'lines',
  });

  const watchedLines = useWatch({ control, name: 'lines', defaultValue: [] });

  const linesForRender = useMemo(
    () => (fields ?? []).map((field, index) => {
      const watched = watchedLines?.[index] ?? {};
      return {
        ...field,
        ...watched,
        id: field.id,
      };
    }),
    [fields, watchedLines],
  );

  const initialSelectedRows = useMemo(
    () => linesForRender
      .filter((line) => line.mtrId)
      .map((line) => ({
        optionMtrId: line.mtrId,
        mtrNo: line.mtrNo,
        name: line.mtrName,
      })),
    [linesForRender],
  );

  function handleOpenPicker() {
    setPickerKey((prev) => prev + 1);
    setPickerOpen(true);
  }

  function handlePickerConfirm(selectedRows) {
    const nextLines = mergePurchaseOrderLinesFromPickerSelection(
      getValues('lines') || [],
      selectedRows,
    );
    replace(nextLines);
  }

  return (
    <section className="@container/form-section pb-6">
      <div className="mb-3">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="form-block__title mb-0!">採購明細</h2>
          <Button type="button" size="sm" onClick={handleOpenPicker}>
            選料
          </Button>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">{PURCHASE_ORDER_LINES_TIP}</p>
      </div>

      {errors?.lines?.message ? (
        <p className="mb-3 text-sm text-red-500">{errors.lines.message}</p>
      ) : null}

      {linesForRender.length === 0 ? (
        <p className="text-sm text-muted-foreground">尚無明細；請選料加入。</p>
      ) : (
        <div
          className={cn(
            'create-edit-panel-scroll overflow-x-auto rounded-md border border-gray-300',
            LINES_MIN_W,
          )}
        >
          <div className={LINE_HEADER_CLASS} aria-hidden>
            <span>物料</span>
            <span>單位</span>
            <span>訂購數量</span>
            <span>單價</span>
            <span>行備註</span>
            <span className="sr-only">操作</span>
          </div>
          <ul className="m-0 list-none divide-y divide-gray-200 p-0">
            {linesForRender.map((line, index) => (
              <PurchaseOrderLineRow
                key={line.id}
                index={index}
                control={control}
                register={register}
                errors={errors}
                line={line}
                setValue={setValue}
                onRemove={remove}
              />
            ))}
          </ul>
        </div>
      )}

      <MtrPickerDialog
        key={pickerKey}
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        initialSelectedRows={initialSelectedRows}
        onConfirm={handlePickerConfirm}
        title="請選擇採購物料"
        description="多選後確認；已存在於明細者不重複加入。"
      />
    </section>
  );
};

PurchaseOrderLinesEditor.propTypes = {
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object,
  getValues: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
};

export default PurchaseOrderLinesEditor;
